import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const Auth = () => {
  const { login, API } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    full_name: "",
    country: "Ecuador",
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      login(response.data.access_token, response.data.user);
      toast.success("Bienvenido de nuevo!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar sesion");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      login(response.data.access_token, response.data.user);
      toast.success("Cuenta creada exitosamente!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrarse");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success("Revisa tu email para restablecer tu contrasena");
    } catch (error) {
      toast.error("Error al enviar el email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />

      <div className="min-h-screen flex items-center justify-center px-4 pt-24 pb-12">
        <Card className="w-full max-w-md bg-card/50 backdrop-blur-md border-white/10">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-white text-center">
              <img src="/logo-v4.png" alt="CONTAFLIX" className="h-9 w-auto mx-auto" />
            </CardTitle>
            <CardDescription className="text-center text-white/60">
              {showForgotPassword ? "Recuperar contrasena" : "Accede a tu cuenta o registrate"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showForgotPassword ? (
              <div className="space-y-4">
                {forgotSent ? (
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 mx-auto bg-[#E50914]/10 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-[#E50914]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-white text-lg font-semibold">Email enviado</p>
                    <p className="text-white/60 text-sm">
                      Si <strong className="text-white">{forgotEmail}</strong> esta registrado, recibiras un enlace para restablecer tu contrasena. Revisa tu bandeja de entrada y spam.
                    </p>
                    <Button
                      onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(""); }}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10 w-full"
                      data-testid="back-to-login-btn"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Volver a iniciar sesion
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <p className="text-white/60 text-sm">
                      Ingresa tu email y te enviaremos un enlace para restablecer tu contrasena.
                    </p>
                    <div>
                      <Label htmlFor="forgot-email" className="text-white">Email</Label>
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder="tu@email.com"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                        className="bg-white/5 border-white/10 focus:border-[#E50914]/50 text-white placeholder:text-white/30 h-12"
                        data-testid="forgot-email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold h-12"
                      data-testid="forgot-submit-btn"
                    >
                      {loading ? "Enviando..." : "Enviar enlace"}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setShowForgotPassword(false)}
                      variant="ghost"
                      className="w-full text-white/60 hover:text-white hover:bg-white/5"
                      data-testid="cancel-forgot-btn"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Volver
                    </Button>
                  </form>
                )}
              </div>
            ) : (
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">Iniciar Sesion</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Registrarse</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email" className="text-white">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="tu@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 focus:border-[#E50914]/50 text-white placeholder:text-white/30 h-12"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password" className="text-white">Contrasena</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 focus:border-[#E50914]/50 text-white placeholder:text-white/30 h-12"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold h-12"
                    data-testid="login-submit-btn"
                  >
                    {loading ? "Cargando..." : "Iniciar Sesion"}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="w-full text-center text-sm text-white/50 hover:text-[#E50914] transition-colors cursor-pointer"
                    data-testid="forgot-password-link"
                  >
                    Olvidaste tu contrasena?
                  </button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="register-name" className="text-white">Nombre Completo</Label>
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="Juan Perez"
                      value={registerData.full_name}
                      onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 focus:border-[#E50914]/50 text-white placeholder:text-white/30 h-12"
                      data-testid="register-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-email" className="text-white">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="tu@email.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 focus:border-[#E50914]/50 text-white placeholder:text-white/30 h-12"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-password" className="text-white">Contrasena</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 focus:border-[#E50914]/50 text-white placeholder:text-white/30 h-12"
                      data-testid="register-password-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-country" className="text-white">Pais</Label>
                    <Input
                      id="register-country"
                      type="text"
                      placeholder="Ecuador"
                      value={registerData.country}
                      onChange={(e) => setRegisterData({ ...registerData, country: e.target.value })}
                      className="bg-white/5 border-white/10 focus:border-[#E50914]/50 text-white placeholder:text-white/30 h-12"
                      data-testid="register-country-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold h-12"
                    data-testid="register-submit-btn"
                  >
                    {loading ? "Cargando..." : "Crear Cuenta"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
